/**
 * Hook for fetching city list
 * @module hooks/use-cities
 */

import { useState, useEffect, useCallback } from 'react';
import { getCities } from '@/lib/api/initiatives';
import type { CityLookup } from '@/types/initiative';

export interface UseCitiesReturn {
  cities: CityLookup[];
  loading: boolean;
  error: string | null;
}

export function useCities(): UseCitiesReturn {
  const [cities, setCities] = useState<CityLookup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCities = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getCities();
      if (response.success && response.data) {
        setCities(response.data);
      } else {
        setError(response.message || 'Failed to load cities');
        setCities([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      setCities([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCities();
  }, [fetchCities]);

  return { cities, loading, error };
}
